import { CH_HOME_FEATURE } from '@propertypal/shared/src/constants/content-hub';
import { getNlpUrl } from '@propertypal/shared/src/reducers/search';
import { trackGaEvent, trackGaLink, trackSearch } from '@propertypal/shared/src/services/analytics';
import { HomepageTakeover } from '@propertypal/shared/src/types/homepageTakeover';
import { Property } from '@propertypal/shared/src/types/property';
import { ConvertedContentHubArticle } from '@propertypal/shared/src/types/strapi/overrides';
import { SuperFeatureProperty } from '@propertypal/shared/src/types/superFeature';
import platform from '@propertypal/shared/src/utils/platform';
import { Filters } from '@propertypal/shared/src/utils/search/getNlpFilters';
import ImageSlideshow from '@propertypal/web-ui/src/animation/ImageSlideshow';
import FeaturesTrio from '@propertypal/web-ui/src/home/FeaturesTrio';
import GuidesBar from '@propertypal/web-ui/src/home/GuidesBar';
import LatestNews from '@propertypal/web-ui/src/home/LatestNews';
import SimilarProperties from '@propertypal/web-ui/src/property/SimilarProperties';
import Takeover from '@propertypal/web-ui/src/takeover/Takeover';
import router from 'next/router';
import React, { FunctionComponent, useState } from 'react';
import { useTheme } from 'styled-components';
import featuresTrioData from '../../constants/homepage/featuresTrioData';
import usePropertyFavourite from '../../hooks/usePropertyFavourite';
import HomeSearchBar from '../search/HomeSearchBar';
import { TabButton, TabRow } from '../search/HomeSearchBar.style';

interface Props {
  superfeatures: SuperFeatureProperty[];
  recent: Property[];
  popular: Property[];
  takeover: HomepageTakeover | null;
  articles: ConvertedContentHubArticle[] | null;
  cfIpCountry: string | null;
  loggedIn: boolean;
}

const SALE_TYPES = [
  { tab: 'sales', filters: { category: 'residential', saleType: 'sale' }, extraParams: 'pt=residential&st=sale' },
  { tab: 'rent', filters: { category: 'residential', saleType: 'rent' }, extraParams: 'pt=residential&st=rent' },
  { tab: 'newhomes', filters: { category: 'newhomes', saleType: 'sale' }, extraParams: 'pt=newhomes&st=sale' },
];

const HomePage: FunctionComponent<Props> = (props) => {
  const images = props.superfeatures.map((row) => row.url || row.image.url);
  const highResImages = props.superfeatures.map((row) => row.url || row.image?.urls['1600x1067:FILL_CROP']);
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const onFavourite = usePropertyFavourite(props.loggedIn);
  const [category, setCategory] = useState('sales');
  const saleType = SALE_TYPES.find((s) => s.tab === category);

  const handleSubmit = async (baseUrl: string, text: string, filters: Filters) => {
    const button = category === 'newhomes' ? 'New Homes' : category === 'rent' ? 'For Rent' : 'For Sale';
    setLoading(true);
    const url = await getNlpUrl(text, { ...filters, ...saleType?.filters });

    if (url) {
      trackGaEvent('nav_click', {
        page: 'HP',
        location: 'Search Box',
        button,
      });
      trackSearch('HP', text, filters);

      await router.push(baseUrl + url);
    }
  };

  const searchBar = (
    <div style={{ width: '100%', maxWidth: 740 }}>
      <TabRow>
        <TabButton $active={category === 'sales'} onClick={() => setCategory('sales')} type="button">
          For Sale
        </TabButton>

        <TabButton $active={category === 'rent'} onClick={() => setCategory('rent')} type="button">
          For Rent
        </TabButton>

        <TabButton $active={category === 'newhomes'} onClick={() => setCategory('newhomes')} type="button">
          New Homes
        </TabButton>
      </TabRow>

      <HomeSearchBar
        showTravelPreview
        showSaleTypeOptions
        onSubmit={handleSubmit}
        loading={loading}
        extraNlpParams={saleType?.extraParams}
      />
    </div>
  );

  return (
    <>
      {props.takeover && props.takeover.listing ? (
        <Takeover takeover={props.takeover}>{searchBar}</Takeover>
      ) : (
        <ImageSlideshow
          images={images || []}
          highResImages={highResImages}
          slideDuration={10000}
          superFeatureProperties={props.superfeatures}
          highResThreshold={1600}
        >
          {searchBar}
        </ImageSlideshow>
      )}

      <SimilarProperties
        properties={props.popular}
        title="Most Viewed Properties"
        titleSize={36}
        subTitle="(Last 7 days)"
        showArrows
        showFooter
        onClick={trackGaLink('nav_click', 'Most Viewed', 'HP')}
        onFavourite={onFavourite}
      />

      <FeaturesTrio heading={`House Hunting is Easier with ${platform.title}`} data={featuresTrioData} />

      {props?.articles && props?.articles?.length > 0 && (
        <>
          <LatestNews articles={props.articles} />

          {CH_HOME_FEATURE && <GuidesBar />}
        </>
      )}

      {props.recent && (
        <SimilarProperties
          properties={props.recent}
          titleSize={36}
          title="Recently Added Properties"
          showArrows
          showFooter
          onClick={trackGaLink('nav_click', 'Recently Added', 'HP')}
          {...(props?.articles && props?.articles?.length > 0 && !CH_HOME_FEATURE
            ? { arrowBg: theme.white, background: theme.backgroundLightest }
            : {})}
          onFavourite={onFavourite}
        />
      )}
    </>
  );
};

export default HomePage;
